import {
  BatchedMesh,
  Matrix4,
  Vector3,
  type BufferGeometry,
  type Camera,
  type Material,
} from 'three';

export interface BirdParts {
  readonly bodies: readonly number[];
  readonly leftWings: readonly number[];
  readonly rightWings: readonly number[];
  readonly feet: readonly number[];
  readonly shapes: readonly number[];
}

const matrix = new Matrix4(),
  cameraPosition = new Vector3(),
  birdPosition = new Vector3();

/** Distant silhouettes fall below the painting's stroke size; keep their instances out of the draw. */
export function selectBirds(mesh: BatchedMesh, parts: BirdParts, camera: Camera): void {
  cameraPosition.setFromMatrixPosition(camera.matrixWorld);
  const roofStart = parts.bodies.length - parts.feet.length;
  parts.bodies.forEach((body, i) => {
    mesh.getMatrixAt(body, matrix);
    birdPosition.setFromMatrixPosition(matrix).applyMatrix4(mesh.matrixWorld);
    const visible = birdPosition.distanceToSquared(cameraPosition) < 400 ** 2;
    mesh.setVisibleAt(body, visible);
    mesh.setVisibleAt(parts.leftWings[i]!, visible);
    mesh.setVisibleAt(parts.rightWings[i]!, visible);
    if (i >= roofStart) mesh.setVisibleAt(parts.feet[i - roofStart]!, visible);
  });
}

/** Independent rigid parts share one draw without mirrored transforms or changing their geometry. */
export function createBirdBatch(
  shapes: readonly BufferGeometry[],
  count: number,
  perched: number,
  material: Material
): { mesh: BatchedMesh; parts: BirdParts } {
  const mesh = new BatchedMesh(
    count * 3 + perched,
    shapes.reduce((sum, shape) => sum + shape.getAttribute('position').count, 0),
    shapes.reduce((sum, shape) => sum + (shape.index?.count ?? 0), 0),
    material
  );
  mesh.name = 'waterbird-parts';
  mesh.frustumCulled = false;
  mesh.perObjectFrustumCulled = false;
  mesh.sortObjects = false;
  const ids = shapes.map((shape) => {
    const id = mesh.addGeometry(shape);
    shape.dispose();
    return id;
  });
  const instances = (shape: number, length: number) =>
    Array.from({ length }, () => mesh.addInstance(ids[shape]!));
  const parts: BirdParts = {
    bodies: instances(0, count),
    leftWings: instances(1, count),
    rightWings: instances(2, count),
    feet: perched ? instances(3, perched) : [],
    shapes: ids,
  };
  mesh.userData['birdParts'] = parts;
  const draw = mesh.onBeforeRender.bind(mesh);
  mesh.onBeforeRender = (...args) => {
    selectBirds(mesh, parts, args[2]);
    draw(...args);
  };
  return { mesh, parts };
}
